
import queries from "../../gql/queries";
import { defineComponent } from "vue";
import { PurchaseOrderListing } from "@/models/PurchaseOrder";
import MixinPOstatus from "@/mixins/POStatus";
import MixinFormatDate from "@/mixins/DateFormatting";
import StatusBadge from "@/components/StatusBadge.vue";
import { hasPermission, SystemPermissions } from "@/models/User";

interface FilterOption {
	text: string;
	value: string;
}

export default defineComponent({
	name: "PurchaseOrderList",
	components: {
		StatusBadge,
	},
	mixins: [MixinPOstatus, MixinFormatDate],
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
				selectedFilters: [] as string[],
			},
			purchaseOrders: [] as PurchaseOrderListing[],
			totalCount: 0,
			searchTimeout: null as ReturnType<typeof setTimeout> | null,
			loadingFromQP: false,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["orderDate"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			filterOptions: [
				{ text: "Open Orders", value: "onlyOpenOrder" },
				{ text: "Manual Orders", value: "onlyManualOrder" },
				{ text: "Dropship Orders", value: "onlyDropShipOrder" },
				{ text: "Cancelled Orders", value: "onlyCancelledOrder" },
			] as FilterOption[],
			headers: [
				{
					text: "PO #",
					value: "orderNumber",
					sortable: true,
					width: "10%",
				},
				{
					text: "Order Date",
					value: "orderDate",
					sortable: true,
					width: "10%",
				},
				{
					text: "Style",
					value: "styleCode",
					sortable: true,
					width: "10%",
				},
				{
					text: "Vendor",
					value: "vendor",
					sortable: true,
					width: "19%",
				},
				{
					text: "Order Status",
					value: "orderStatus",
					sortable: true,
					width: "14%",
				},
				{
					text: "Processing",
					value: "orderType",
					sortable: true,
					width: "10%",
				},
				{
					text: "Received",
					value: "received",
					sortable: true,
					width: "7%",
				},
				{
					text: "Expected",
					value: "expectedDate",
					sortable: true,
					width: "6%",
				},
				{
					text: "Dropship",
					value: "dropShip",
					sortable: true,
					width: "5%",
					align: "center",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "9%",
					align: "center",
				},
			],
		};
	},

	apollo: {
		purchaseOrdersQuery: {
			query: () => queries.SEARCH_PURCHASE_ORDER_ITEMS,
			variables() {
				return this.buildQueryVariables();
			},
			update(data: any) {
				this.loading = false;
				this.totalCount = data.response?.totalCount || 0;
				this.purchaseOrders = data.response?.items || [];
				return data.response;
			},
			error(error: any) {
				console.error("Error fetching purchase orders:", error);
				this.loading = false;
			},
		},
	},

	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection: string) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/purchaseOrdersListState"];
					if (savedState) {
						// Restore page from offset
						const page = Math.floor(savedState.offset / this.tableOptions.itemsPerPage) + 1;
						this.tableOptions.page = page;
						this.searchForm.selectedFilters = [...savedState.filters];
						this.searchForm.keywords = savedState.searchTerm;
					}
				}
			},
			immediate: true,
		},
		"$route.query.purchaseOrderNumber": {
			handler(newPurchaseOrderNumber: string) {
				if (newPurchaseOrderNumber) {
					this.loadOrderFromQueryParam();
				}
			},
			immediate: true,
		},
	},

	computed: {
		navigationDirection(): string {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},

	methods: {
		formatDate(date: string) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		canCreatePO() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_PurchaseOrders_Create)) {
				return true;
			}
			return user.isAdmin;
		},

		canPrintPO() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_PurchaseOrders_Print)) {
				return true;
			}
			return user.isAdmin;
		},

		handlePrintPreview(data: PurchaseOrderListing) {
			this.$router.push(`/purchasing/purchase-orders/${data.purchaseOrderId}/print`);
		},

		handleView(data: PurchaseOrderListing) {
			this.$router.push(`/purchasing/purchase-orders/${data.purchaseOrderId}`);
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterClear() {
			this.$nextTick(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			});
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue: string) {
			const sortFieldMap: Record<string, string> = {
				orderNumber: "purchaseOrderNumber",
				orderDate: "purchaseOrderDate",
				styleCode: "styleCode",
				vendor: "vendor",
				orderStatus: "orderStatus",
				orderType: "orderType",
				received: "received",
				expectedDate: "expectedDate",
				dropShip: "dropShip",
			};
			return sortFieldMap[columnValue] || "purchaseOrderDate";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Keyword search is routed through the `searchTerm` argument, where the
			// resolver applies FTS on StyleCode/Vendor and LIKE on PurchaseOrderNumber.
			// Only pass 3+ char keywords; shorter terms short-circuit server-side.
			// HotChocolate's `where.contains` path is non-sargable (CHARINDEX + empty-LIKE),
			// so we avoid it for keyword search. Non-keyword filters (status, etc.) still
			// use `where` below.
			const where: any = { and: [] };
			const searchTerm = keyword.length >= 3 ? keyword : "";

			// Add filter conditions
			if (this.searchForm.selectedFilters.includes("onlyOpenOrder")) {
				where.and.push({ received: { lt: 1 }, isCancelled: { eq: false } });
			}
			if (this.searchForm.selectedFilters.includes("onlyManualOrder")) {
				where.and.push({
					or: [
						{ purchaseOrderNumber: { startsWith: "7" } },
						{ purchaseOrderNumber: { startsWith: "9" } },
					],
				});
			}
			if (this.searchForm.selectedFilters.includes("onlyDropShipOrder")) {
				where.and.push({ dropShip: { eq: true } });
			}
			if (this.searchForm.selectedFilters.includes("onlyCancelledOrder")) {
				where.and.push({ isCancelled: { eq: true } });
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "orderDate";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm,
				where: where.and.length > 0 ? where : null,
			};
		},

		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.purchaseOrdersQuery) {
				await this.$apollo.queries.purchaseOrdersQuery.refetch();
			}
		},

		async searchWithRetry(maxRetries: number) {
			let retries = 0;
			while (retries < maxRetries) {
				await this.$apollo.queries.purchaseOrdersQuery?.refetch();
				await this.$nextTick();

				let purchaseOrderNumber = this.$route.query.purchaseOrderNumber;
				if (purchaseOrderNumber && this.purchaseOrders && this.purchaseOrders.length > 0) {
					const order = this.purchaseOrders.find(
						(q: PurchaseOrderListing) => q.orderNumber === purchaseOrderNumber
					);
					if (order) {
						this.$router.push(`/purchasing/purchase-orders/${order.purchaseOrderId}`);
						return;
					}
				}

				retries++;
				console.log(`Retry attempt ${retries}`);
				await new Promise((resolve) => setTimeout(resolve, 250));
			}

			console.log("Max retries reached. Order not found.");
		},

		async loadOrderFromQueryParam() {
			let purchaseOrderNumber = this.$route.query.purchaseOrderNumber;
			if (purchaseOrderNumber) {
				this.searchForm.keywords = purchaseOrderNumber as string;
				this.loadingFromQP = true;
				await this.searchWithRetry(10);
				this.loadingFromQP = false;
			}
		},
	},

	beforeRouteLeave(to, from, next) {
		const offset = (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage;
		this.$store.dispatch("globalState/savePurchaseOrdersListState", {
			offset,
			filters: [...this.searchForm.selectedFilters],
			searchTerm: this.searchForm.keywords,
			scrollPosition: window.scrollY,
		});
		next();
	},

	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
});
